import { useEffect, useMemo } from "react";
import {
  AccessibilityInfo,
  StyleSheet,
  Text,
  View,
  type ViewStyle,
} from "react-native";

import { radius, space, textStyle } from "@passfirst/ui";

import { type Colors, useColors } from "../../theme";
import type { SubmitResult } from "../types";

interface Props {
  result: SubmitResult;
  /** Slotted under the explanation body — typically the report-section. */
  footer?: React.ReactNode;
}

// Post-answer reveal — green-toned header for correct, brick for
// wrong. Caller passes the footer slot (report trigger / sent state /
// open report panel) so this stays presentational.
export function ExplanationCard({ result, footer }: Props): JSX.Element {
  const colors = useColors();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const headerStyle: ViewStyle["backgroundColor"] = result.correct
    ? colors.textCorrect
    : colors.textWrong;
  // Announce the result to screen readers when it first appears — the
  // live region below covers re-renders, but the explicit announce
  // guarantees the verdict + reasoning is read on reveal.
  useEffect(() => {
    AccessibilityInfo.announceForAccessibility(
      `${result.correct ? "Correct" : "Incorrect"}. ${result.explanation}`,
    );
  }, [result]);
  return (
    <View style={styles.explanation} accessibilityLiveRegion="polite">
      <Text style={[styles.explanationHeader, { color: headerStyle }]}>
        {result.correct ? "Correct ✓" : "Incorrect"}
      </Text>
      <Text style={styles.explanationBody}>{result.explanation}</Text>
      {footer}
    </View>
  );
}

function makeStyles(colors: Colors) {
  return StyleSheet.create({
    explanation: {
      padding: space[4],
      borderRadius: radius.md,
      backgroundColor: colors.surfaceMuted,
      gap: space[1],
    },
    explanationHeader: {
      ...textStyle.label,
      fontFamily: textStyle.h3.fontFamily,
    },
    explanationBody: {
      ...textStyle.bodySmall,
      color: colors.textSecondary,
      lineHeight: 21,
    },
  });
}
